/**
 * React Hook for BPM Monitoring
 */

import { useState, useEffect, useCallback } from 'react';
import { bpmService, BPMReading, BPMZone } from '@/services/bpm-service';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';

interface UseBPMReturn {
  currentBPM: number;
  readings: BPMReading[];
  avgBPM: number;
  maxBPM: number;
  minBPM: number;
  zone: BPMZone | null;
  isMonitoring: boolean;
  showConnectionDialog: boolean;
  setShowConnectionDialog: (show: boolean) => void;
  requestConnection: () => void;
  startMonitoring: (sessionId: string) => Promise<void>;
  stopMonitoring: () => Promise<BPMReading[]>;
  addManualReading: (bpm: number) => void;
  status: 'idle' | 'scanning' | 'connecting' | 'connected' | 'error' | 'error_samsung';
}

/**
 * IMPORTANT: Pass real user age instead of default 30
 * Use useUserAge() or useUserHealthProfile() to get the actual age
 * Example:
 *   const userAge = useUserAge();
 *   const bpm = useBPM(userAge);
 */
export function useBPM(userAge: number = 30): UseBPMReturn {
  const { toast } = useToast();
  const [currentBPM, setCurrentBPM] = useState(0);
  const [readings, setReadings] = useState<BPMReading[]>([]);
  const [avgBPM, setAvgBPM] = useState(0);
  const [maxBPM, setMaxBPM] = useState(0);
  const [minBPM, setMinBPM] = useState(0);
  const [zone, setZone] = useState<BPMZone | null>(null);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [showConnectionDialog, setShowConnectionDialog] = useState(false);
  const [status, setStatus] = useState<'idle' | 'scanning' | 'connecting' | 'connected' | 'error' | 'error_samsung'>('idle');

  const requestConnection = useCallback(() => {
    setShowConnectionDialog(true);
  }, []);

  const startMonitoring = useCallback(async (sessionId: string) => {
    try {
      // Don't set isMonitoring=true yet, wait for 'connected' status
      await bpmService.startMonitoring(sessionId);
      
      toast({
        title: 'Heart Rate Monitoring',
        description: 'Scanning for devices...',
      });
    } catch (error) {
      logger.error('Failed to start BPM monitoring', error, {
        component: 'useBPM',
        action: 'startMonitoring'
      });
      setIsMonitoring(false); // Ensure false on error
      toast({
        variant: 'destructive',
        title: 'Monitoring Error',
        description: 'Failed to start heart rate monitoring',
      });
    }
  }, [toast]);

  const stopMonitoring = useCallback(async () => {
    try {
      const finalReadings = await bpmService.stopMonitoring();
      setIsMonitoring(false);
      return finalReadings;
    } catch (error) {
      logger.error('Failed to stop BPM monitoring', error, {
        component: 'useBPM',
        action: 'stopMonitoring'
      });
      throw error;
    }
  }, []);

  const addManualReading = useCallback((bpm: number) => {
    bpmService.addManualReading(bpm);
  }, []);

  // Listen to status changes from bpmService
  useEffect(() => {
    const handleStatusChange = (newStatus: string) => {
      setStatus(newStatus as typeof status);
      
      // Critical: Reset isMonitoring when connection fails or goes idle
      if (newStatus === 'error' || newStatus === 'error_samsung' || newStatus === 'idle') {
        setIsMonitoring(false);
      }
      
      // Only set isMonitoring=true when actually connected
      if (newStatus === 'connected') {
        setIsMonitoring(true);
        toast({
          title: '✅ Monitor Conectado',
          description: 'Monitorando frequência cardíaca em tempo real',
        });
      } else if (newStatus === 'reconnecting') {
        toast({
          title: '🔄 Reconectando...',
          description: 'Tentando restaurar conexão com monitor',
        });
      } else if (newStatus === 'error' || newStatus === 'error_samsung') {
        toast({
          variant: 'destructive',
          title: '❌ Erro de Conexão',
          description: newStatus === 'error_samsung' 
            ? 'Monitor Samsung detectado. Veja as instruções.'
            : 'Não foi possível conectar ao monitor cardíaco',
        });
      }
    };
    
    bpmService.onStatusChange(handleStatusChange);

    // Listen to real-time BPM readings (optional: for debugging)
    bpmService.onBPMReading((reading) => {
      logger.debug('BPM reading received', { 
        bpm: reading.bpm, 
        source: reading.source 
      });
    });

    // Handle connection loss with auto-reconnect
    bpmService.onConnectionLost(() => {
      toast({
        variant: 'destructive',
        title: '⚠️ Conexão Perdida',
        description: 'Tentaremos reconectar automaticamente',
        duration: 5000,
      });
    });
  }, [toast]);

  // Update BPM and metrics periodically
  useEffect(() => {
    if (!isMonitoring) return;

    const interval = setInterval(() => {
      const current = bpmService.getCurrentBPM();
      setCurrentBPM(current);
      setReadings(bpmService.getAllReadings());
      setAvgBPM(bpmService.getAverageBPM());
      setMaxBPM(bpmService.getMaxBPM());
      setMinBPM(bpmService.getMinBPM());
      
      if (current > 0) {
        setZone(bpmService.getBPMZone(current, userAge));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isMonitoring, userAge]);

  return {
    currentBPM,
    readings,
    avgBPM,
    maxBPM,
    minBPM,
    zone,
    isMonitoring,
    showConnectionDialog,
    setShowConnectionDialog,
    requestConnection,
    startMonitoring,
    stopMonitoring,
    addManualReading,
    status
  };
}
